import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, CardBody, Card, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Badge, Box } from '@mantine/core';
import { ODataGrid } from '../../../Components/OElements/ODataGrid';
import { getBreachRecordListQuery, getBreachRecord, updateBreachRecord } from '../../../slices/breachRecord/thunk';
import ViewDetallesIncumplimiento from './Details/ViewDetallesIncumplimiento';
import AddEditIncumplimiento from '../Incumplimientos/Add_Edit/AddEditIncumplimiento';
import { getUserList } from '../../../slices/user/thunk';
import { getInspectionTypeList } from '../../../slices/inspectionType/thunk';
import { getInspectionAuthorityList } from '../../../slices/inspectionAuthority/thunk';
import { getBreachCategoryList } from '../../../slices/breachCategory/thunk';
import { getBreachOriginList } from '../../../slices/breachOrigin/thunk';
import { getBreachImpactList } from '../../../slices/breachImpact/thunk';
import { getBreachResponsibleAreaList } from '../../../slices/breachResponsibleArea/thunk';
import { getBreachRiskClassificationList } from '../../../slices/breachRiskClassification/thunk';
import { getBreachTypeOfEvidenceList } from '../../../slices/breachTypeOfEvidence/thunk';

const getUserDisplayName = user =>
	user?.full_name ||
	[user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
	user?.name ||
	user?.username ||
	'';

const getRiesgoBadgeColor = label => {
	const l = (label || '').toLowerCase();
	if (l.includes('alto')) return 'red';
	if (l.includes('medio')) return 'yellow';
	if (l.includes('bajo')) return 'green';
	return 'gray';
};

const getStatusBadgeColor = label => {
	const l = (label || '').toLowerCase();
	if (l.includes('no cumple')) return 'red';
	if (l.includes('parcialmente')) return 'yellow';
	if (l.includes('cumple')) return 'green';
	if (l.includes('pendiente')) return 'blue';
	return 'gray';
};

const normalizeStr = s => (s || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();

const getActivityStatusBadgeColor = label => {
	const l = normalizeStr(label);
	if (l.includes('aprobado')) return 'green';
	if (l.includes('rechazado')) return 'red';
	if (l.includes('pendiente')) return 'yellow';
	if (l.includes('revision') || l.includes('resvision')) return 'blue';
	return 'gray';
};

const getAuthorityStatusBadgeColor = label => {
	const l = (label || '').normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase();
	if (l.includes('no cumple')) return 'red';
	if (l.includes('parcialmente')) return 'yellow';
	if (l.includes('revision')) return 'blue';
	if (l.includes('cumple')) return 'green';
	if (l.includes('pendiente')) return 'gray';
	return 'gray';
};

const toStringId = value => (value == null ? '' : value.toString());

const normalizeBreachRecord = item => ({
	...item,
	_id: toStringId(item?._id),
	inspection_type_id: toStringId(item?.inspection_types?._id || item?.inspection_type?._id),
	breach_responsible_area_id: toStringId(item?.breach_responsible_area?._id),
	responsible_user_ids: Array.isArray(item?.responsible_user_ids)
		? item.responsible_user_ids.map(toStringId)
		: Array.isArray(item?.responsible_users) && item.responsible_users.length > 0
			? item.responsible_users.map(u => toStringId(u._id))
			: item?.responsible_user_id
				? [toStringId(item.responsible_user_id)]
				: [],
	breach_impact_id: toStringId(item?.breach_impact?._id),
	breach_type_of_evidence_id: Array.isArray(item?.breach_type_of_evidence_id)
		? item.breach_type_of_evidence_id.map(toStringId)
		: Array.isArray(item?.breach_type_of_evidences) && item.breach_type_of_evidences.length > 0
			? item.breach_type_of_evidences.map(e => toStringId(e._id))
			: item?.breach_type_of_evidence?._id
				? [toStringId(item.breach_type_of_evidence._id)]
				: [],
	breach_category_id: toStringId(item?.breach_category?._id),
	breach_origin_id: toStringId(item?.breach_origin?._id),
	breach_risk_classification_id: toStringId(item?.breach_risk_classification?._id),
	breach_status_id: toStringId(item?.breach_status?._id),
	authority_status_id: toStringId(item?.authority_status?._id),
	activity_status_id: toStringId(item?.activity_status?._id),
	inspection_authority_ids: Array.isArray(item?.inspection_authority_ids)
		? item.inspection_authority_ids.map(toStringId)
		: Array.isArray(item?.inspection_authorities) && item.inspection_authorities.length > 0
			? item.inspection_authorities.map(a => toStringId(a._id))
			: item?.inspection_authority_id
				? [toStringId(item.inspection_authority_id)]
				: item?.inspection_authority?._id
					? [toStringId(item.inspection_authority._id)]
					: [],
});

const ActividadesList = ({ inspectionId, externalRefetch = 0, onStatusChange }) => {
	const dispatch = useDispatch();
	const { user: profileUser } = useSelector(state => state.Profile);
	const { userList } = useSelector(state => state.Users);
	const { inspectionTypeListSelect } = useSelector(state => state.InspectionType);
	const { inspectionAuthorityListSelect } = useSelector(state => state.InspectionAuthority);
	const { breachCategoryListSelect } = useSelector(state => state.BreachCategory);
	const { breachOriginListSelect } = useSelector(state => state.BreachOrigin);
	const { breachImpactListSelect } = useSelector(state => state.BreachImpact);
	const { breachResponsibleAreaListSelect } = useSelector(state => state.BreachResponsibleArea);
	const { breachRiskClassificationListSelect } = useSelector(state => state.BreachRiskClassification);
	const { breachTypeOfEvidenceListSelect } = useSelector(state => state.BreachTypeOfEvidence);
	const { currentBreachRecord, isLoadingCurrent } = useSelector(state => state.BreachRecord);

	const [dataActividad, setDataActividad] = useState({});
	const [modalView, setModalView] = useState(false);
	const [modalMode, setModalMode] = useState('breach');
	const [refetchRemoteData, setRefetchRemoteData] = useState(0);
	const [modalEdit, setModalEdit] = useState(false);
	const [dataIncumplimiento, setDataIncumplimiento] = useState({});
	const [pendingEditOpen, setPendingEditOpen] = useState(false);

	const responsibleOptions = useMemo(
		() =>
			(userList || []).map(item => ({
				item,
				value: toStringId(item._id),
				label: getUserDisplayName(item),
			})),
		[userList]
	);

	useEffect(() => {
		if (!modalEdit) return;
		dispatch(getUserList());
		dispatch(getInspectionTypeList());
		dispatch(getInspectionAuthorityList());
		dispatch(getBreachCategoryList());
		dispatch(getBreachOriginList());
		dispatch(getBreachImpactList());
		dispatch(getBreachResponsibleAreaList());
		dispatch(getBreachRiskClassificationList());
		dispatch(getBreachTypeOfEvidenceList());
	}, [dispatch, modalEdit]);

	useEffect(() => {
		if (pendingEditOpen && currentBreachRecord && !isLoadingCurrent) {
			setDataIncumplimiento(normalizeBreachRecord(currentBreachRecord));
			setModalEdit(true);
			setPendingEditOpen(false);
		}
	}, [currentBreachRecord, isLoadingCurrent, pendingEditOpen]);

	const handleClickVerDetalles = useCallback(data => {
		setDataActividad(data);
		setModalMode('breach');
		setModalView(true);
	}, []);

	const handleClickEstadoAutoridad = useCallback(data => {
		setDataActividad(data);
		setModalMode('authority');
		setModalView(true);
	}, []);

	const handleClickEdit = useCallback(data => {
		setPendingEditOpen(true);
		dispatch(getBreachRecord(toStringId(data._id)));
	}, [dispatch]);

	const handleSaveIncumplimiento = async values => {
		const actor =
			profileUser?.username ||
			profileUser?.email ||
			profileUser?._id ||
			profileUser?.id ||
			'system';

		const resultAction = await dispatch(
			updateBreachRecord({
				...values,
				breach_status_id: dataIncumplimiento.breach_status_id || '',
				activity_status_id: dataIncumplimiento.activity_status_id || '',
				authority_status_id: dataIncumplimiento.authority_status_id || '',
				modification_user: actor,
				_id: dataIncumplimiento._id,
			})
		);

		if (resultAction.type.endsWith('/rejected')) return;

		setModalEdit(false);
		setRefetchRemoteData(prev => prev + 1);
		onStatusChange?.();
	};

	const columns = useMemo(
		() => [
			{
				header: 'Acciones',
				enableSorting: false,
				enableColumnFilter: false,
				enableGlobalFilter: false,
				enableColumnActions: false,
				accessorKey: 'actions',
				columnDefType: 'display',
				size: 'auto',
				Cell: cellProps => {
					const rowData = cellProps.row.original;
					return (
						<React.Fragment>
							<div className='d-flex justify-content-center'>
								<div className='flex-shrink-0'>
									<ul className='list-inline mb-0'>
										<li className='list-inline-item'>
											<Link
												title='Ver Detalles'
												to='#'
												onClick={() => handleClickVerDetalles(rowData)}
											>
												<i className='ri-eye-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												title='Respuesta Autoridad'
												to='#'
												onClick={() => handleClickEstadoAutoridad(rowData)}
											>
												<i className='ri-shield-check-line align-bottom me-2 text-muted'></i>
											</Link>
										</li>
										<li className='list-inline-item'>
											<Link
												title='Editar'
												to='#'
												onClick={() => handleClickEdit(rowData)}
											>
												<i className='ri-pencil-fill align-bottom me-2 text-muted'></i>
											</Link>
										</li>
									</ul>
								</div>
							</div>
						</React.Fragment>
					);
				},
			},
			{
				header: 'Descripción',
				dataType: 'string',
				accessorKey: 'description',
				size: 250,
				Cell: cellProps => {
					const val = cellProps.row.original.description;
					if (!val) return '-';
					return (
						<div
							style={{
								maxWidth: '300px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
							title={val}
						>
							{val}
						</div>
					);
				},
			},
			{
				header: 'Estado',
				dataType: 'select',
				accessorKey: 'breach_status.name',
				Cell: cellProps => {
					const label = cellProps.row.original.breach_status?.name;
					if (!label) return '-';
					return (
						<Badge color={getStatusBadgeColor(label)} variant='light' tt='capitalize'>
							{label}
						</Badge>
					);
				},
			},
			{
				header: 'Estado Actividad',
				dataType: 'select',
				accessorKey: 'activity_status.name',
				Cell: cellProps => {
					const label = cellProps.row.original.activity_status?.name;
					if (!label) return '-';
					return (
						<Badge color={getActivityStatusBadgeColor(label)} variant='light' tt='capitalize'>
							{label}
						</Badge>
					);
				},
			},
			{
				header: 'Respuesta Autoridad',
				dataType: 'select',
				accessorKey: 'authority_status.name',
				Cell: cellProps => {
					const label = cellProps.row.original.authority_status?.name;
					if (!label) return '-';
					return (
						<Badge color={getAuthorityStatusBadgeColor(label)} variant='light' tt='capitalize'>
							{label}
						</Badge>
					);
				},
			},
			{
				header: 'Categoría',
				dataType: 'select',
				accessorKey: 'breach_category.name',
			},
			{
				header: 'Entidad Fiscalizadora',
				dataType: 'select',
				accessorKey: 'inspection_authorities.name',
				Cell: cellProps => {
					const authorities = cellProps.row.original.inspection_authorities || [];
					return (
						<div className='d-flex flex-wrap gap-1 justify-content-center'>
							<Badge variant='light' tt='capitalize' size='xs'>
								{authorities.name || '-'}
							</Badge>
						</div>
					);
				}
			},
			{
				header: 'Origen',
				dataType: 'select',
				accessorKey: 'breach_origin.name',
			},
			{
				header: 'Fecha',
				dataType: 'date',
				accessorKey: 'breach_date',
			},
			{
				header: 'Riesgo',
				dataType: 'select',
				accessorKey: 'breach_risk_classification.name',
				Cell: cellProps => {
					const label = cellProps.row.original.breach_risk_classification?.name;
					if (!label) return '-';
					return (
						<Badge color={getRiesgoBadgeColor(label)} variant='light' tt='capitalize'>
							{label}
						</Badge>
					);
				},
			},
			{
				header: 'Medida Correctiva',
				dataType: 'string',
				accessorKey: 'corrective_measure',
				size: 250,
				Cell: cellProps => {
					const val = cellProps.row.original.corrective_measure;
					if (!val) return '-';
					return (
						<div
							style={{
								maxWidth: '300px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
							title={val}
						>
							{val}
						</div>
					);
				},
			},
			{
				header: 'Tipo Fiscalización',
				dataType: 'select',
				accessorKey: 'inspection_types.name',
			},
			{
				header: 'Área Responsable',
				dataType: 'select',
				accessorKey: 'breach_responsible_area.name',
			},
			{
				id: 'responsible_user',
				header: 'Responsable',
				dataType: 'string',
				accessorFn: row => {
					const users = row.responsible_users;
					if (Array.isArray(users) && users.length > 0) {
						return users.map(getUserDisplayName).filter(Boolean).join(', ') || '-';
					}
					return getUserDisplayName(row.responsible_user) || '-';
				},
			},
			{
				header: 'Tipo Evidencia',
				dataType: 'select',
				accessorKey: 'breach_type_of_evidence.name',
				Cell: cellProps => {
					const row = cellProps.row.original;
					if (Array.isArray(row.breach_type_of_evidences) && row.breach_type_of_evidences.length > 0) {
						return row.breach_type_of_evidences.map(e => e.name).filter(Boolean).join(', ') || '-';
					}
					return row.breach_type_of_evidence?.name || '-';
				},
			},


		],

		[handleClickVerDetalles, handleClickEstadoAutoridad, handleClickEdit]
	);

	return (
		<React.Fragment>
			<Box
				className='p-2'
				sx={{
					display: 'Grid',
					margin: 'auto',
					width: '100%',
				}}
			>
				<ODataGrid
					columns={columns}
					getDataFunc={getBreachRecordListQuery}
					refetchRemoteData={refetchRemoteData + externalRefetch}
					baseFilters={{
						disable: { $eq: false },
						inspections_id: { $eq: inspectionId },
					}}
					queryPopulate={[
						'inspection_type',
						'inspection_authority',
						'breach_responsible_area',
						'breach_origin',
						'breach_type_of_evidence',					'breach_type_of_evidences',						'breach_category',
						'breach_risk_classification',
						'breach_status',
						'activity_status',
						'authority_status',
						'responsible_user'
					]}
					remoteOperations={{
						pagination: true,
						sorting: true,
						filtering: true,
					}}
					getRowIdTable={row => row._id}
					tableTitle='Lista de Actividades'
				/>
			</Box>

			<ViewDetallesIncumplimiento
				dataActividad={dataActividad}
				isOpen={modalView}
				handleToggleModal={() => setModalView(false)}
				onStatusChange={() => {
					setRefetchRemoteData(prev => prev + 1);
					onStatusChange?.();
				}}
				statusMode={modalMode}
			/>

			<AddEditIncumplimiento
				dataIncumplimiento={dataIncumplimiento}
				dataFiscalizacion={null}
				isOpen={modalEdit}
				handleToggleModal={() => setModalEdit(false)}
				handleSave={handleSaveIncumplimiento}
				isEdit={true}
				inspectionTypeOptions={inspectionTypeListSelect}
				inspectionAuthorityOptions={inspectionAuthorityListSelect}
				responsibleOptions={responsibleOptions}
				breachResponsibleAreaOptions={breachResponsibleAreaListSelect}
				breachImpactOptions={breachImpactListSelect}
				breachOriginOptions={breachOriginListSelect}
				breachTypeOfEvidenceOptions={breachTypeOfEvidenceListSelect}
				breachCategoryOptions={breachCategoryListSelect}
				breachRiskClassificationOptions={breachRiskClassificationListSelect}
			/>
		</React.Fragment>
	);
};

export default ActividadesList;
